/**
 * 🟥 CUADRO NEGRO Genio Premium Gate (feb-2026) + 🟥 CUADRO NEGRO 2026-02
 * Animaciones Premium del Modal del Genio.
 *
 * Modal mostrado cuando un usuario NO Premium intenta abrir el Genio
 * (Lyon IA) o cuando una llamada al backend de Lyon devuelve
 * 403 {"detail": "premium_required"}.
 *
 * Mensaje EXACTO (sin variaciones):
 *   "Esta función está disponible solo para usuarios Premium"
 *
 * CTA principal: "Actualizar a Premium" → openSubscriptionPanel().
 * CTA secundaria: "Cerrar".
 *
 * Se monta vía portal en document.body para evitar conflictos con
 * z-index del header / panel flotante (Lyon vive en z-[9999]).
 *
 * 🟥 ANIMACIONES (Cuadro Negro 2026-02): overlay con blur dinámico
 * fade-in (240ms), modal con scale-in suave (260ms), glow violeta
 * dinámico infinito (4s), contenido fade-in (260ms), y close
 * animado scale-out + fade-out (180ms) antes de unmount.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Crown, Sparkles, CreditCard, Check } from 'lucide-react';
import { openSubscriptionPanel } from '../utils/openSubscriptionPanel';
import { PREMIUM_BENEFITS } from '../constants/premiumBenefits';

export const GENIO_PREMIUM_MESSAGE = 'Esta función está disponible solo para usuarios Premium';

// Duración del cierre — coincide con `genieModalFadeOut` y
// `genieModalScaleOut` (180ms ease-out forwards).
const CLOSE_ANIM_MS = 180;

export const GenioPremiumGateModal = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  // Reset isClosing cuando el modal se vuelve a abrir.
  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen]);

  // Cierre animado: dispara animación de salida y luego llama a onClose.
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, CLOSE_ANIM_MS);
  }, [isClosing, onClose]);

  // ESC dispara el cierre animado.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  // 🟥 CUADRO NEGRO Panel de Suscripción Único (feb-2026):
  // "Actualizar a Premium" y "Ver mi plan" abren EL MISMO destino — el
  // Panel de Suscripción real con los botones $9 y $86 sobre el Área de
  // Trabajo. Sin Stripe Billing Portal, sin redirect a páginas
  // informativas.
  const handleUpgrade = () => {
    handleClose();
    setTimeout(() => openSubscriptionPanel(), CLOSE_ANIM_MS + 80);
  };

  const handleViewMyPlan = () => {
    handleClose();
    setTimeout(() => openSubscriptionPanel(), CLOSE_ANIM_MS + 80);
  };

  return createPortal(
    <>
      {/* 🟥 Overlay con fade-in + blur dinámico. Click cierra animado. */}
      <div
        className={`genie-overlay${isClosing ? ' closing' : ''}`}
        onClick={handleClose}
        data-testid="genio-premium-modal-overlay"
      />

      {/* 🟥 Contenedor del modal — scale-in + glow violeta dinámico.
          Las clases Tailwind `w-full md:w-[80%]` y `max-h-[90vh] md:max-h-[70vh]`
          se mantienen por compatibilidad con tests legacy; el CSS del
          Cuadro Negro 2026-02 (`.genie-modal-container { width:92%; ... }`)
          gana visualmente vía cascada. */}
      <div
        role="dialog"
        aria-modal="true"
        data-testid="genio-premium-modal"
        className={`genie-modal-container w-full md:w-[80%] max-h-[90vh] md:max-h-[70vh]${isClosing ? ' closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="genie-modal-content">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-fuchsia-400 to-amber-400" />

          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            data-testid="genio-premium-gate-close-btn"
            className="absolute top-3 right-3 p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Scrollable body — la lista de beneficios puede ser larga
              (§3.3 padding 16px + scroll si excede). */}
          <div className="flex-1 overflow-y-auto p-4 pt-7 md:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-fuchsia-500/10 border border-amber-500/40 flex items-center justify-center text-amber-300">
                <Crown className="w-7 h-7" />
              </div>
              <div className="min-w-0">
                <h3
                  className="text-lg font-semibold text-white leading-tight"
                  data-testid="genio-premium-gate-title"
                >
                  {GENIO_PREMIUM_MESSAGE}
                </h3>
                <p className="text-sm text-zinc-400 mt-2">
                  Esta acción avanzada requiere Plan Premium. El chat con
                  Lyon IA sigue disponible para vos — podés seguir
                  consultando, pedir ayuda y conversar siempre que quieras.
                </p>
              </div>
            </div>

            {/* Beneficios oficiales — fuente única `PREMIUM_BENEFITS`. */}
            <ul
              className="mb-4 space-y-1.5"
              data-testid="genio-premium-gate-benefits"
            >
              {PREMIUM_BENEFITS.map((text) => (
                <li
                  key={text}
                  data-testid="genio-premium-benefit"
                  className="flex items-start gap-2 text-xs text-zinc-300"
                >
                  <Check className="shrink-0 w-3.5 h-3.5 mt-0.5 text-amber-300" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-2">
              <button
                type="button"
                onClick={handleViewMyPlan}
                data-testid="genio-premium-gate-view-plan-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-200 hover:text-white bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-colors"
              >
                <CreditCard className="w-4 h-4" />
                Ver mi plan
              </button>
              <button
                type="button"
                onClick={handleClose}
                data-testid="genio-premium-gate-dismiss-btn"
                className="flex-1 px-4 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 border border-zinc-700 transition-colors"
              >
                Seguir con Lyon IA
              </button>
              <button
                type="button"
                onClick={handleUpgrade}
                data-testid="genio-premium-gate-upgrade-btn"
                className="flex-[2] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-zinc-950 bg-gradient-to-r from-amber-400 to-fuchsia-400 hover:from-amber-300 hover:to-fuchsia-300 shadow-lg shadow-amber-500/20 transition-all"
              >
                <Sparkles className="w-4 h-4" />
                Actualizar a Premium
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🟥 CUADRO NEGRO 2026-02 — Estilos premium del modal del Genio.
          NO TOCAR duraciones, easing, keyframes, blur ni glow sin un
          nuevo Cuadro Negro. */}
      <style>{`
        .genie-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          opacity: 0;
          z-index: 10010;
          animation: genieModalFadeIn 240ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .genie-overlay.closing {
          animation: genieModalFadeOut 180ms ease-out forwards;
        }
        .genie-modal-container {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #0f0f0f;
          border-radius: 18px;
          border: 1px solid #2a2a2a;
          box-shadow:
            0 0 22px rgba(168, 85, 247, 0.18),
            0 0 44px rgba(56, 189, 248, 0.12);
          opacity: 0;
          width: 92%;
          max-width: 420px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          z-index: 10011;
          animation:
            genieModalScaleIn 260ms cubic-bezier(0.16, 1, 0.3, 1) forwards,
            genieBorderGlow 4s ease-in-out infinite 260ms;
        }
        .genie-modal-container.closing {
          animation: genieModalScaleOut 180ms ease-out forwards;
        }
        .genie-modal-content {
          position: relative;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          animation: genieContentFade 260ms ease-out forwards;
        }
        @keyframes genieModalFadeIn {
          0%   { opacity: 0; backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
          100% { opacity: 1; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
        }
        @keyframes genieModalFadeOut {
          0%   { opacity: 1; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
          100% { opacity: 0; backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
        }
        @keyframes genieModalScaleIn {
          0%   { transform: translate(-50%, -50%) scale(0.88) translateY(12px); opacity: 0; }
          60%  { transform: translate(-50%, -50%) scale(1.04) translateY(0);   opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1)    translateY(0);   opacity: 1; }
        }
        @keyframes genieModalScaleOut {
          0%   { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(0.92); opacity: 0; }
        }
        @keyframes genieBorderGlow {
          0%   { box-shadow: 0 0 22px rgba(168, 85, 247, 0.18), 0 0 44px rgba(56, 189, 248, 0.12); }
          50%  { box-shadow: 0 0 28px rgba(168, 85, 247, 0.28), 0 0 52px rgba(56, 189, 248, 0.16); }
          100% { box-shadow: 0 0 22px rgba(168, 85, 247, 0.18), 0 0 44px rgba(56, 189, 248, 0.12); }
        }
        @keyframes genieContentFade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body,
  );
};

export default GenioPremiumGateModal;
